import * as React from 'react';
import { Input, makeStyles, tokens } from '@fluentui/react-components';
import { defineMessage, defineMessages, useIntl } from 'react-intl';
import { SelectField } from '@foundershub/components';

interface Props {
  gender?: string | null;
  label?: string;
  onChange: (gender: string) => void;
}

export function GenderSelector({ gender = '', label, onChange }: Props) {
  const intl = useIntl();
  const labelText = label || intl.formatMessage(defaultLabel);
  const classes = useStyles();

  const [enableFreeform, setEnableFreeform] = React.useState<boolean>(
    gender !== null && gender !== '' && !nonFreeformGenders.includes(gender)
  );

  return (
    <div className={classes.root}>
      <SelectField
        label={labelText}
        defaultValue={!enableFreeform ? (gender && gender !== freeformValue ? gender : '') : freeformValue}
        onChange={(event, data) => {
          setEnableFreeform(data.value === freeformValue);
          onChange(data.value === freeformValue ? '' : data.value);
        }}
        placeholder={intl.formatMessage(placeholderLabel)}
        required={false}
      >
        <option disabled value="">
          {intl.formatMessage(placeholderLabel)}
        </option>
        {Object.entries(genderOptions).map(([key, message]) => (
          <option key={key} value={key}>
            {intl.formatMessage(message)}
          </option>
        ))}
      </SelectField>

      {enableFreeform ? (
        <Input
          aria-label={intl.formatMessage(preferredLabel)}
          defaultValue={gender && !Object.values(GenderEnum).some((genderKey) => genderKey === gender) ? gender : ''}
          onChange={(event, data) => {
            onChange(data.value);
          }}
          placeholder={intl.formatMessage(preferredLabel)}
        />
      ) : null}
    </div>
  );
}

const freeformValue = 'Prefer to self describe as';

export const GenderEnum = {
  Male: 'Male',
  Female: 'Female',
  PreferNotToSay: 'Prefer not to say',
  PreferToSelfDescribeAs: freeformValue,
};

export const genderOptions = defineMessages({
  [GenderEnum.Male]: {
    defaultMessage: 'Male',
    description: 'Gender option in select box',
  },
  [GenderEnum.Female]: {
    defaultMessage: 'Female',
    description: 'Gender option in select box',
  },
  [GenderEnum.PreferNotToSay]: {
    defaultMessage: 'Prefer not to say',
    description: 'Gender option in select box',
  },
  [GenderEnum.PreferToSelfDescribeAs]: {
    defaultMessage: 'Prefer to self describe as',
    description: 'Gender option in select box (prefix/label for free-form text box)',
  },
});
const nonFreeformGenders = Object.keys(genderOptions).filter((key) => key !== freeformValue);

const defaultLabel = defineMessage({
  defaultMessage: 'What’s your self-identified gender?',
  description: 'Label for selecting gender / self-identity',
});

const preferredLabel = defineMessage({
  defaultMessage: 'Include your preferred identity',
  description: 'Placeholder for a free-form text box to enter gender identity',
});

const placeholderLabel = defineMessage({
  defaultMessage: 'Select',
  description: 'Placeholder to select a gender from a dropdown',
});

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
});
